import { Suspense, useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';

import './App.scss';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-circular-progressbar/dist/styles.css';
import 'react-medium-image-zoom/dist/styles.css';
import '@fontsource/archivo-black';
import '@fontsource/dm-sans';
import AboutTab from './components/AboutTab';
import CustomIdleTimer from './components/custom_components/CustomIdleTimer';
import SubUserTab from './components/custom_components/SubUserTab/SubuserTab';
import ExamHint from './components/ExamHint';
import ForgotPassword from './components/Login/ForgotPassword';
import LoginForm from './components/Login/LoginForm';
import ResetPasswordWithOTP from './components/Login/ResetPasswordWithOTP';
import NewPatientHeader from './components/NewPatientHeader/NewPatientHeader';
import NotFound from './components/NotFound';
import SessionPreview from './components/SessionPreview/SessionPreview';
import SettingsTab from './components/SettingsTab/SettingsTab';
import useCheckClientVersion from './hooks/useCheckClientVersion';
import useGetCurrentUser from './hooks/useGetCurrentUser';
import useWatchDeployment from './hooks/useWatchDeployment';
import { Application } from './models/Application.model';
import { Camera } from './models/Camera.model';
import { CameraMode } from './models/CameraMode.model';
import { initData } from './models/InitialData.model';
import { Product, ProductFullName } from './models/Product.model';
import AIIntegratedResults from './products/AIIntegrated/pages/AIIntegratedResults/AIIntegratedResults';
import { PrivateRoute } from './routes';
import Sentry from './services/sentry';
import useRemoteBoundStore from './store/useRemoteBoundStore';

if ('$' in window) {
  window.$(() => {
    // dom ready
    window.bootbox.setDefaults({
      closeButton: false,
      animate: false,
    });
    window.bootbox.addLocale('vi', {
      OK: 'Xác nhận',
      CANCEL: 'Hủy bỏ',
      CONFIRM: 'Xác nhận',
    });
  });
}

function App() {
  const [init] = useState<boolean>(initData.init);
  const [app] = useState<Application>(initData.app);

  useCheckClientVersion();

  const [ipList, setIpList] = useState<string[]>([]);

  const {
    authInfo: { authToken },
    setCameraMode,
  } = useRemoteBoundStore();
  const { startDeploymentWatcher, stopDeploymentWatcher } =
    useWatchDeployment();

  useEffect(() => {
    if (document.title === ProductFullName(app.product)) {
      return;
    }

    // app icon
    const iconLink = document.createElement('link');
    iconLink.rel = 'icon';
    document.head.append(iconLink);
    iconLink.href =
      app.product === Product.OPTAIN
        ? '/icons/app-optain.ico'
        : '/icons/app.ico';

    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    document.head.append(appleLink);
    appleLink.href =
      app.product === Product.OPTAIN
        ? '/icons/app-optain.png'
        : '/icons/app.png';

    // app name
    document.title = ProductFullName(app.product);

    setCameraMode(
      app.camera === Camera.SMALL ? CameraMode.API : CameraMode.UPLOAD,
    );
  }, [app.product, app.camera, init, setCameraMode]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      startDeploymentWatcher();
    }
    return () => {
      stopDeploymentWatcher();
    };
  }, [startDeploymentWatcher, stopDeploymentWatcher]);

  const { userId } = useGetCurrentUser();

  Sentry.setTags({
    product: app.product,
    camera: app.camera,
    userId,
  });

  return (
    <>
      <Suspense fallback={null}>
        <Switch>
          <Route path="/login" strict exact>
            <LoginForm />
          </Route>

          <Route path="/forgot-password" strict exact>
            <ForgotPassword />
          </Route>

          <Route path="/reset-password-with-otp/:email" strict exact>
            <ResetPasswordWithOTP />
          </Route>

          <PrivateRoute path="/" strict exact>
            <NewPatientHeader />
          </PrivateRoute>

          <PrivateRoute path="/session-form" strict exact>
            <NewPatientHeader />
          </PrivateRoute>

          <PrivateRoute path="/session-hint" strict exact>
            <ExamHint />
          </PrivateRoute>

          <PrivateRoute path="/session-preview" strict exact>
            <SessionPreview />
          </PrivateRoute>

          <PrivateRoute path="/results" strict exact>
            <AIIntegratedResults />
          </PrivateRoute>

          <PrivateRoute path="/settings" strict exact>
            <SettingsTab ipList={ipList} setIpList={setIpList} />
          </PrivateRoute>

          <PrivateRoute path="/about" strict exact>
            <AboutTab />
          </PrivateRoute>

          <PrivateRoute path="/subuser" strict exact>
            <SubUserTab />
          </PrivateRoute>

          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </Suspense>

      {authToken && <CustomIdleTimer />}
    </>
  );
}

export default App;
